<script setup lang="ts">
import type { SwitchProps } from "./switch.types";
import { useSwitch } from "./useSwitch";
import { VisuallyHiddenInput } from "components/VisuallyHidden";

defineOptions({ name: "ESwitch" });

const props = withDefaults(defineProps<SwitchProps>(), {
  color: "neutral",
  variant: "subtle",
  size: "md",
  modelValue: false,
  disabled: false,
  required: false,
  encased: false,
});

const {
  state,
  buttonRef,
  rootClasses,
  trackClasses,
  thumbClasses,
  ariaChecked,
  ariaDisabled,
  dataDisabled,
  dataState,
  formValue,
  handleClick,
  WrapperComponent,
} = useSwitch({ props });

defineExpose({ buttonRef });
</script>

<template>
  <component
    :is="WrapperComponent"
    :color="state.encased?.value ? (state.variant?.value === 'solid' ? 'mono' : state.color?.value) : undefined"
    :variant="state.encased?.value ? state.variant?.value : undefined"
    :size="state.encased?.value ? 'sm' : undefined"
    :class="state.encased?.value ? 'p-0!' : undefined"
  >
    <!-- part="base" is the inner layout container; when encased=true the host's first child is <e-card>, not this div -->
    <div part="base" :class="rootClasses" @click="handleClick">
      <button
        ref="buttonRef"
        type="button"
        role="switch"
        part="track"
        :class="trackClasses"
        :aria-checked="ariaChecked"
        :aria-disabled="ariaDisabled"
        :data-disabled="dataDisabled"
        :data-state="dataState"
      >
        <span part="thumb" :class="thumbClasses" />
      </button>

      <slot />

      <visually-hidden-input
        :name="state.name?.value"
        :value="formValue"
        :required="state.required?.value"
        :disabled="state.disabled?.value"
        type="checkbox"
      />
    </div>
  </component>
</template>
